
import { useState, useRef } from "react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useApiClient } from "@/lib/api";
import { FileUp, Trash2, FileCheck, FileText } from "lucide-react";

const Resume = () => {
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [resumePreview, setResumePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { api } = useApiClient();

  const handleFileDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    handleFileSelected(file);
  };

  const handleFileSelected = (file: File | null) => {
    if (!file) return;

    // Check if the file is a PDF or Word document
    if (
      file.type === "application/pdf" ||
      file.type === "application/msword" ||
      file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    ) {
      setResumeFile(file);
      // Create a preview URL for PDF files
      if (file.type === "application/pdf") {
        setResumePreview(URL.createObjectURL(file));
      } else {
        setResumePreview(null);
      }
    } else {
      toast.error("Please upload a PDF or Word document");
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFileSelected(file);
  };

  const handleUpload = async () => {
    if (!resumeFile) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", resumeFile);

      // In a real application, this would be an API call
      // await api.uploadResume(formData);

      // Simulate API call
      setTimeout(() => {
        setIsUploaded(true);
        setIsUploading(false);
        toast.success("Resume uploaded successfully!");
      }, 1500);
    } catch (error) {
      console.error("Error uploading resume:", error);
      toast.error("Failed to upload resume. Please try again.");
      setIsUploading(false);
    }
  };

  const clearFile = () => {
    setResumeFile(null);
    setResumePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Resume</h1>
          <p className="text-gray-600 mt-1">
            Upload your resume to help generate tailored interview questions
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Upload Your Resume</CardTitle>
              <CardDescription>
                For the best results, upload a detailed and up-to-date resume
                (PDF or Word format)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isUploaded ? (
                <div className="border-2 border-green-500 border-dashed rounded-lg p-6 bg-green-50 flex flex-col items-center justify-center text-center">
                  <FileCheck className="h-12 w-12 text-green-500 mb-4" />
                  <h3 className="text-lg font-semibold text-green-700">
                    Resume Successfully Uploaded
                  </h3>
                  <p className="text-sm text-green-600 mt-2">
                    Your resume has been processed and is ready for interview question
                    generation.
                  </p>

                  <Button
                    variant="outline"
                    className="mt-4"
                    onClick={() => setIsUploaded(false)}
                  >
                    Upload a New Version
                  </Button>
                </div>
              ) : (
                <>
                  <div
                    className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={handleFileDrop}
                    onClick={triggerFileInput}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.doc,.docx"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                    {resumeFile ? (
                      <div className="flex flex-col items-center">
                        {resumePreview ? (
                          <div className="mb-4 max-h-60 overflow-hidden border rounded">
                            <iframe
                              src={resumePreview}
                              className="w-full h-60"
                              title="Resume Preview"
                            />
                          </div>
                        ) : (
                          <FileText className="h-12 w-12 text-interview-blue mb-4" />
                        )}
                        <h3 className="text-lg font-semibold">
                          {resumeFile.name}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          {(resumeFile.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                        <div className="flex gap-3 mt-4">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              clearFile();
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Remove
                          </Button>
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleUpload();
                            }}
                            disabled={isUploading}
                          >
                            {isUploading ? "Uploading..." : "Upload Resume"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <FileUp className="h-12 w-12 text-gray-400 mb-4" />
                        <h3 className="text-lg font-semibold">
                          Drag and drop your resume here
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Or click to select a file
                        </p>
                        <p className="text-xs text-gray-400 mt-4">
                          Supported formats: PDF, DOC, DOCX
                        </p>
                        <Button className="mt-4">Select File</Button>
                      </div>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Resume Tips</CardTitle>
              <CardDescription>
                How to optimize your resume for interviews
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold text-interview-blue">
                  Be Specific With Skills
                </h3>
                <p className="text-sm text-gray-600">
                  List specific skills that match the job description for better
                  question generation.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-interview-blue">
                  Highlight Achievements
                </h3>
                <p className="text-sm text-gray-600">
                  Include measurable achievements from previous roles to discuss
                  during interviews.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-interview-blue">
                  Keep It Current
                </h3>
                <p className="text-sm text-gray-600">
                  Regularly update your resume with your most recent experience
                  and skills.
                </p>
              </div>

              <div>
                <h3 className="font-semibold text-interview-blue">
                  Tailor For Each Job
                </h3>
                <p className="text-sm text-gray-600">
                  Customize your resume to match each specific job application
                  for better results.
                </p>
              </div>
            </CardContent>
            <CardFooter>
              <Button variant="outline" className="w-full">
                View Resume Guide
              </Button>
            </CardFooter>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Resume;
